export const USER_FILTER_ALL = "__all__";
export const USER_FILTER_EMPTY = "—";
export const ACTIVE_STATUS_FILTER_KEY = "activeStatus";
export const ACTIVE_STATUS_ACTIVE = "active";
export const ACTIVE_STATUS_INACTIVE = "inactive";

export const CONTACT_FILTER_OPTIONS = [
  { value: USER_FILTER_ALL, label: "All" },
  { value: "Primary", label: "Primary" },
  { value: "Yes", label: "Yes" },
  { value: USER_FILTER_EMPTY, label: "–" },
];

export const ACTIVE_STATUS_FILTER_OPTIONS = [
  { value: USER_FILTER_ALL, label: "All statuses" },
  { value: ACTIVE_STATUS_ACTIVE, label: "Active" },
  { value: ACTIVE_STATUS_INACTIVE, label: "Inactive" },
];

export function getDefaultUserTableFilters(isVendorContext = false) {
  return {
    title: USER_FILTER_ALL,
    activeStatus: ACTIVE_STATUS_ACTIVE,
    role: USER_FILTER_ALL,
    portalAccess: USER_FILTER_ALL,
    ...(isVendorContext
      ? {
          vqqContact: USER_FILTER_ALL,
          assessmentContact: USER_FILTER_ALL,
        }
      : {}),
  };
}

/**
 * Filters used only to load the filter-option catalog and unfiltered totals.
 * Matches prototype behavior: options come from the full dataset, not the
 * currently filtered/paginated table page.
 */
export function getCatalogUserTableFilters(isVendorContext = false) {
  return {
    ...getDefaultUserTableFilters(isVendorContext),
    activeStatus: USER_FILTER_ALL,
  };
}

/** Resolve the selected filter option, keeping a synthetic entry if the value is missing from options. */
export function getSelectedFilterOption(filter, value) {
  const matched = filter?.options?.find((option) => option.value === value);
  if (matched) return matched;
  if (value != null && value !== USER_FILTER_ALL) {
    return { value, label: String(value) };
  }
  return filter?.options?.[0];
}

/** Ensure the currently selected value remains visible in the dropdown menu. */
export function getFilterMenuOptions(filter, selectedValue) {
  const options = filter?.options ?? [];
  if (
    selectedValue == null ||
    selectedValue === USER_FILTER_ALL ||
    options.some((option) => option.value === selectedValue)
  ) {
    return options;
  }
  return [...options, { value: selectedValue, label: String(selectedValue) }];
}

export function nextTriStateSortConfig(prev, key) {
  const currentDirection = prev?.[key]?.direction;
  if (!currentDirection) {
    return { [key]: { direction: "asc" } };
  }
  if (currentDirection === "asc") {
    return { [key]: { direction: "desc" } };
  }
  return {};
}

export function getUserDisplayName(item) {
  return [item?.first_name, item?.last_name].filter(Boolean).join(" ").trim() || USER_FILTER_EMPTY;
}

export function getUserTitleValue(item) {
  return item?.job_title?.trim?.() || item?.title?.trim?.() || USER_FILTER_EMPTY;
}

export function getUserRoleValue(item) {
  const sponsorRoles = item?.sponsor_subrole_dto?.map?.((role) => role?.sub_role).filter(Boolean);
  const vendorRoles = item?.vendor_subrole_dto?.map?.((role) => role?.sub_role).filter(Boolean);
  const subRoles = item?.sub_roles?.filter?.(Boolean);
  const roles = sponsorRoles?.length ? sponsorRoles : vendorRoles?.length ? vendorRoles : subRoles?.length ? subRoles : item?.role ? [item.role] : [];
  return roles.length ? roles.join("/") : USER_FILTER_EMPTY;
}

export function getUserStatusValue(item) {
  return item?.is_active ? ACTIVE_STATUS_ACTIVE : ACTIVE_STATUS_INACTIVE;
}

export function getCreatedValue(item) {
  return item?.created_at ?? item?.created_date ?? item?.create_date_time ?? "";
}

export function normalizeFilterOptionValue(value) {
  if (value == null) return USER_FILTER_EMPTY;
  const trimmed = String(value).trim();
  return trimmed === "" ? USER_FILTER_EMPTY : trimmed;
}

export function buildDistinctOptions(items, getValue, allLabel) {
  const seen = new Set();
  const options = [];

  (items ?? []).forEach((item) => {
    const normalized = normalizeFilterOptionValue(getValue(item));
    if (!seen.has(normalized)) {
      seen.add(normalized);
      options.push({ value: normalized, label: normalized });
    }
  });

  options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

  return [{ value: USER_FILTER_ALL, label: allLabel }, ...options];
}

export function buildUserTableFilterDefinitions({
  items,
  roleLabel = "Role",
  portalAccessLabel = "Portal Access",
  getPortalAccessValue,
  isVendorContext = false,
  getVqqContactValue,
  getAssessmentContactValue,
}) {
  const definitions = [
    {
      key: "title",
      label: "Title",
      allLabel: "All titles",
      defaultValue: USER_FILTER_ALL,
      options: buildDistinctOptions(items, getUserTitleValue, "All titles"),
    },
    {
      key: ACTIVE_STATUS_FILTER_KEY,
      label: "Active Status",
      allLabel: "All statuses",
      defaultValue: ACTIVE_STATUS_ACTIVE,
      showPillWhenDefault: true,
      options: ACTIVE_STATUS_FILTER_OPTIONS,
    },
    {
      key: "role",
      label: roleLabel,
      allLabel: `All ${roleLabel.toLowerCase()}s`,
      defaultValue: USER_FILTER_ALL,
      options: buildDistinctOptions(items, getUserRoleValue, `All ${roleLabel.toLowerCase()}s`),
    },
    {
      key: "portalAccess",
      label: portalAccessLabel,
      allLabel: "All access levels",
      defaultValue: USER_FILTER_ALL,
      options: buildDistinctOptions(items, getPortalAccessValue, "All access levels"),
    },
  ];

  if (isVendorContext) {
    definitions.push(
      {
        key: "vqqContact",
        label: "VQQ Contact",
        allLabel: "All",
        defaultValue: USER_FILTER_ALL,
        options: CONTACT_FILTER_OPTIONS,
      },
      {
        key: "assessmentContact",
        label: "Assessment Contact",
        allLabel: "All",
        defaultValue: USER_FILTER_ALL,
        options: CONTACT_FILTER_OPTIONS,
      }
    );
  }

  return definitions.map((definition) => ({
    ...definition,
    getValue: resolveUserFilterValueGetter(definition.key, {
      getPortalAccessValue,
      getVqqContactValue,
      getAssessmentContactValue,
    }),
  }));
}

function resolveUserFilterValueGetter(key, getters) {
  if (key === "title") return getUserTitleValue;
  if (key === ACTIVE_STATUS_FILTER_KEY) return getUserStatusValue;
  if (key === "role") return getUserRoleValue;
  if (key === "portalAccess") return getters.getPortalAccessValue;
  if (key === "vqqContact") return getters.getVqqContactValue;
  return getters.getAssessmentContactValue;
}

export function isDefaultUserTableFilters(filters, defaults) {
  return Object.keys(defaults).every((key) => filters?.[key] === defaults[key]);
}

export function applyUserTableFilters(items, filters, filterDefinitions) {
  return (items ?? []).filter((item) =>
    filterDefinitions.every((definition) => {
      const selectedValue = filters?.[definition.key] ?? definition.defaultValue;
      if (selectedValue === USER_FILTER_ALL) return true;
      return normalizeFilterOptionValue(definition.getValue(item)) === selectedValue;
    })
  );
}

export function matchesUserSearch(item, searchTerm, includeOrganization = false) {
  if (!searchTerm) return true;
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) return true;

  const haystack = [
    getUserDisplayName(item),
    item?.email,
    getUserTitleValue(item),
    getUserRoleValue(item),
    includeOrganization
      ? item?.provider_name || item?.sponsor_name || item?.organization_name || item?.sponsor_organization_name
      : null,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(normalizedSearch);
}

export function mapTeamUserExportRecord(
  item,
  {
    includeOrganization = false,
    getPortalAccessValue,
    includeVendorContacts = false,
    getVqqContactValue,
    getAssessmentContactValue,
    formatCreated,
  } = {}
) {
  return {
    Name: getUserDisplayName(item),
    ...(includeOrganization
      ? {
          Organization:
            item?.provider_name?.trim?.() ||
            item?.sponsor_name ||
            item?.organization_name ||
            item?.sponsor_organization_name ||
            USER_FILTER_EMPTY,
        }
      : {}),
    Title: getUserTitleValue(item),
    Email: item?.email || USER_FILTER_EMPTY,
    "Active Status": item?.is_active ? "Active" : "Inactive",
    Role: getUserRoleValue(item),
    "Portal Access": getPortalAccessValue?.(item) || USER_FILTER_EMPTY,
    ...(includeVendorContacts
      ? {
          "VQQ Contact": getVqqContactValue?.(item) ?? USER_FILTER_EMPTY,
          "Assessment Contact":
            getAssessmentContactValue?.(item) ?? USER_FILTER_EMPTY,
        }
      : {}),
    Created: formatCreated
      ? formatCreated(getCreatedValue(item))
      : getCreatedValue(item) || USER_FILTER_EMPTY,
  };
}
